import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Mail, Phone } from "lucide-react";
import { MaskedLine, Reveal } from "../components/Motion";
import Ribbon from "../components/Ribbon";

const PORTRAIT = "/api/files/salt-and-stone/hikes/066708d2-bb84-4b55-b398-b28ce0fb7fb4.jpg";
const TOWN = "https://images.pexels.com/photos/26747469/pexels-photo-26747469.jpeg?auto=compress&cs=tinysrgb&w=1600";

const CHAPTERS = [
  {
    n: "01",
    title: "The islander",
    body: "Vasilis Kastritseas grew up on Hydra, where the nearest road is the sea and every errand is a walk. The mule paths above the harbour were his playground; the ridge lines, his classroom. Today he knows every trail on the island first-hand — which bend catches the morning light, which spring still runs in August.",
  },
  {
    n: "02",
    title: "The professional",
    body: "A qualified Physical Education professional and accredited first aider, Vasilis treats a hike the way a coach treats a session: the pace set to the slowest smile, the route matched to the day. Fluent in English and Greek, he guides the way a host walks you home — not a tour, a companion.",
  },
  {
    n: "03",
    title: "Why Hydra",
    body: "No cars. No scooters. Just donkeys, bells and stone. Hydra rises straight out of the Saronic Gulf to the 590-metre summit of Mount Eros, with monasteries folded into its valleys and coves that only a path can reach. It is the rare island where sea and mountain are the same walk.",
  },
  {
    n: "04",
    title: "The promise",
    body: "Small groups — never more than six. Honest grading, real rest stops, and photography pauses where the island actually performs. If the weather turns, the plan bends. You will finish tired in the legs and light everywhere else.",
  },
];

export default function About() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  useEffect(() => {
    document.title = "About Vasilis — Salt & Stone, Hydra";
  }, []);

  return (
    <main data-testid="about-page" className="pt-28 sm:pt-36">
      <header className="mx-auto max-w-7xl px-5 sm:px-8">
        <MaskedLine>
          <span className="overline-label text-terra">About your guide</span>
        </MaskedLine>
        <MaskedLine delay={0.12}>
          <h1 className="display-serif mt-4 max-w-4xl text-5xl leading-[0.95] sm:text-6xl lg:text-7xl" data-testid="about-title">
            Vasilis Kastritseas
          </h1>
        </MaskedLine>
        <MaskedLine delay={0.25}>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-ink/70 sm:text-lg">
            Guide, islander, first aider — and the reason a walk on Hydra becomes
            the day you talk about for years.
          </p>
        </MaskedLine>
      </header>

      <section ref={imgRef} className="mx-auto mt-16 grid max-w-7xl gap-6 px-5 sm:px-8 md:grid-cols-12">
        <Reveal className="md:col-span-5">
          <div className="aspect-[3/4] overflow-hidden rounded-2xl">
            <motion.img style={{ y }} src={PORTRAIT} alt="Vasilis Kastritseas on the trails of Hydra"
              className="h-[110%] w-full object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.15} className="md:col-span-6 md:col-start-7 md:mt-24">
          <div className="aspect-[4/3] overflow-hidden rounded-2xl">
            <img src={TOWN} alt="The harbour of Hydra town" loading="lazy" className="h-full w-full object-cover" />
          </div>
          <p className="mt-3 text-sm uppercase tracking-[0.25em] text-ink/50">
            Hydra Town — every trail starts here
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32" data-testid="about-manifesto">
        <Reveal>
          <span className="overline-label text-terra">The story, in four chapters</span>
        </Reveal>
        <div className="mt-12">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.05}>
              <div className="grid gap-4 border-t border-ink/10 py-12 sm:grid-cols-12 sm:gap-8" data-testid={`about-chapter-${c.n}`}>
                <span className="font-serif text-7xl font-light text-terra/25 sm:col-span-2 sm:text-8xl">{c.n}</span>
                <h2 className="font-serif text-3xl tracking-tight sm:col-span-3 sm:text-4xl">{c.title}</h2>
                <p className="max-w-xl text-lg leading-relaxed text-ink/70 sm:col-span-6 sm:col-start-6 sm:text-lg">
                  {c.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Ribbon />

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="grid gap-12 md:grid-cols-2">
          <Reveal>
            <div>
              <h2 className="display-serif text-4xl leading-none sm:text-5xl">Credentials</h2>
              <ul className="mt-8 space-y-0 border-t border-ink/10" data-testid="about-credentials">
                {[
                  "Qualified Physical Education Professional",
                  "Accredited First Aider",
                  "Experienced Advanced Hiker",
                  "Speciality: out-of-doors adventures",
                  "Fluent English & Greek",
                  "First-hand knowledge of every trail on Hydra",
                ].map((c) => (
                  <li key={c} className="border-b border-ink/10 py-4 text-lg text-ink/75">{c}</li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="flex h-full flex-col justify-between gap-10 bg-deep p-9 text-paper sm:p-12">
              <div>
                <h3 className="font-serif text-3xl tracking-tight sm:text-4xl">Walk with him.</h3>
                <p className="mt-4 max-w-sm text-base leading-relaxed text-paper/65">
                  Prefer to talk it through first? Call or message — Vasilis answers
                  personally, usually between hikes.
                </p>
              </div>
              <div className="flex flex-col gap-5">
                <a href="mailto:hello@saltstonehikes.com" data-testid="about-email"
                  className="flex items-center gap-3 text-lg text-paper transition-colors duration-300 hover:text-terra-light">
                  <Mail size={18} /> hello@saltstonehikes.com
                </a>
                <a href="tel:+306976231058" data-testid="about-phone"
                  className="flex items-center gap-3 text-lg text-paper transition-colors duration-300 hover:text-terra-light">
                  <Phone size={18} /> +(30) 6976 231 058
                </a>
                <Link to="/hikes" data-testid="about-book-btn"
                  className="group inline-flex w-fit items-center gap-2 rounded-full bg-terra px-8 py-4 text-base uppercase tracking-[0.15em] text-paper transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-terra-dark">
                  See the hikes
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
